import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CircleDollarSign, Plus, Trash2, ChevronDown } from 'lucide-react';
import {
  api,
  type BillingLine,
  type BillingSummary,
  type BillingWindowAgg,
  type ClientBilling,
  type Payment,
} from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/Skeletons';
import { cn } from '@/lib/utils';

type Currency = 'USD' | 'EUR';
type PresetId = 'today' | '7d' | '30d' | 'month' | 'prev_month' | 'year' | 'all';

interface Preset {
  id: PresetId;
  label: string;
  /** Возвращает {from, to} в формате YYYY-MM-DD, UTC. */
  range: () => { from: string; to: string };
}

function fmt(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function todayUtc(): Date {
  const n = new Date();
  return new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate()));
}
function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setUTCDate(r.getUTCDate() + n);
  return r;
}

const PRESETS: Preset[] = [
  {
    id: 'today',
    label: 'Сегодня',
    range: () => {
      const t = fmt(todayUtc());
      return { from: t, to: t };
    },
  },
  {
    id: '7d',
    label: '7 дней',
    range: () => ({ from: fmt(addDays(todayUtc(), -6)), to: fmt(todayUtc()) }),
  },
  {
    id: '30d',
    label: '30 дней',
    range: () => ({ from: fmt(addDays(todayUtc(), -29)), to: fmt(todayUtc()) }),
  },
  {
    id: 'month',
    label: 'Текущий месяц',
    range: () => {
      const t = todayUtc();
      const start = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), 1));
      return { from: fmt(start), to: fmt(t) };
    },
  },
  {
    id: 'prev_month',
    label: 'Прошлый месяц',
    range: () => {
      const t = todayUtc();
      const start = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() - 1, 1));
      const end = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), 0));
      return { from: fmt(start), to: fmt(end) };
    },
  },
  {
    id: 'year',
    label: 'Год',
    range: () => {
      const t = todayUtc();
      const start = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
      return { from: fmt(start), to: fmt(t) };
    },
  },
  {
    id: 'all',
    label: 'Всё время',
    range: () => ({ from: '2020-01-01', to: fmt(todayUtc()) }),
  },
];

const DEFAULT_PRESET: PresetId = 'month';

function formatTokens(n: number): string {
  if (n === 0) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toString();
}

function formatMoney(value: number, currency: Currency): string {
  const symbol = currency === 'USD' ? '$' : '€';
  if (value === 0) return `${symbol}0`;
  if (value < 0.01) return `${symbol}<0.01`;
  if (value < 1000) return `${symbol}${value.toFixed(2)}`;
  return `${symbol}${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function emptyAgg(): BillingWindowAgg {
  return {
    input_tokens: 0,
    output_tokens: 0,
    cost_usd: 0,
    cost_eur: 0,
    client_usd: 0,
    client_eur: 0,
  };
}
function addAgg(target: BillingWindowAgg, src: BillingWindowAgg): void {
  target.input_tokens += src.input_tokens;
  target.output_tokens += src.output_tokens;
  target.cost_usd += src.cost_usd;
  target.cost_eur += src.cost_eur;
  target.client_usd += src.client_usd;
  target.client_eur += src.client_eur;
}

function costForCurrency(agg: BillingWindowAgg, currency: Currency): number {
  return currency === 'USD' ? agg.cost_usd : agg.cost_eur;
}
function clientForCurrency(agg: BillingWindowAgg, currency: Currency): number {
  return currency === 'USD' ? agg.client_usd : agg.client_eur;
}
function fixedForCurrency(line: BillingLine, currency: Currency): number {
  return currency === 'USD' ? line.fixed_usd : line.fixed_eur;
}
function lineTotalForCurrency(line: BillingLine, currency: Currency): number {
  return currency === 'USD' ? line.total_usd : line.total_eur;
}

/** Субтотал клиента: LLM клиенту + фикс, в обеих валютах. */
interface ClientTotal {
  client_usd: number;
  client_eur: number;
  fixed_usd: number;
  fixed_eur: number;
}
function totalForCurrency(t: ClientTotal, currency: Currency): number {
  return currency === 'USD'
    ? t.client_usd + t.fixed_usd
    : t.client_eur + t.fixed_eur;
}

/** EUR → USD: usd_to_eur = EUR за 1 USD, значит делим. */
function eurToUsd(eur: number, usdToEur: number): number {
  return usdToEur > 0 ? eur / usdToEur : 0;
}
/** Сумма в EUR → отображение в выбранной валюте. */
function eurDisplay(eur: number, currency: Currency, usdToEur: number): number {
  return currency === 'USD' ? eurToUsd(eur, usdToEur) : eur;
}

function rangeFromPreset(id: PresetId): { from: string; to: string } {
  const preset = PRESETS.find((p) => p.id === id);
  return preset ? preset.range() : PRESETS[3]!.range();
}

export function Billing(): JSX.Element {
  const [currency, setCurrency] = useState<Currency>('EUR');
  const initial = useMemo(() => rangeFromPreset(DEFAULT_PRESET), []);
  const [from, setFrom] = useState<string>(initial.from);
  const [to, setTo] = useState<string>(initial.to);

  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['billing-summary', from, to],
    queryFn: () =>
      api.get<BillingSummary>(`/admin/billing/summary?from=${from}&to=${to}`),
    refetchInterval: 60_000,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['billing-summary'] });
    // Список платежей — отдельный query (['payments', client]); без этого
    // открытая история не обновляется после добавления/удаления платежа.
    void queryClient.invalidateQueries({ queryKey: ['payments'] });
  };
  const priceMutation = useMutation({
    mutationFn: (v: {
      type: string;
      id: string;
      price_eur?: number | null;
      billed_since?: string | null;
      markup_x?: number | null;
      label: string;
      client_name: string;
    }) =>
      api.put(`/admin/connections/${v.type}/${encodeURIComponent(v.id)}/price`, {
        ...(v.price_eur !== undefined ? { price_eur: v.price_eur } : {}),
        ...(v.billed_since !== undefined ? { billed_since: v.billed_since } : {}),
        ...(v.markup_x !== undefined ? { markup_x: v.markup_x } : {}),
        label: v.label,
        client_name: v.client_name,
      }),
    onSuccess: invalidate,
  });

  const addPaymentMutation = useMutation({
    mutationFn: (v: {
      client_name: string;
      amount_eur: number;
      paid_on: string;
      note?: string;
    }) => api.post('/admin/billing/payments', v),
    onSuccess: invalidate,
  });
  const deletePaymentMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/billing/payments/${id}`),
    onSuccess: invalidate,
  });

  // Группировка связок по клиентам + субтотал + биллинг (долг).
  const clientGroups = useMemo(() => {
    if (!data) return [];
    const billingByClient = new Map(
      data.clients_billing.map((b) => [b.client_name, b]),
    );
    const map = new Map<
      string,
      {
        client: string;
        lines: BillingLine[];
        total: ClientTotal;
        billing: ClientBilling | null;
      }
    >();
    for (const line of data.lines) {
      let g = map.get(line.client_name);
      if (!g) {
        g = {
          client: line.client_name,
          lines: [],
          total: { client_usd: 0, client_eur: 0, fixed_usd: 0, fixed_eur: 0 },
          billing: billingByClient.get(line.client_name) ?? null,
        };
        map.set(line.client_name, g);
      }
      g.lines.push(line);
      g.total.client_usd += line.period.client_usd;
      g.total.client_eur += line.period.client_eur;
      g.total.fixed_usd += line.fixed_usd;
      g.total.fixed_eur += line.fixed_eur;
    }
    // Клиенты по убыванию долга (потом по итогу).
    return Array.from(map.values()).sort((a, b) => {
      const da = a.billing?.debt_eur ?? 0;
      const db = b.billing?.debt_eur ?? 0;
      if (db !== da) return db - da;
      return totalForCurrency(b.total, 'EUR') - totalForCurrency(a.total, 'EUR');
    });
  }, [data]);

  // Общий долг по всем клиентам (EUR).
  const totalDebtEur = useMemo(
    () => (data ? data.clients_billing.reduce((s, b) => s + b.debt_eur, 0) : 0),
    [data],
  );

  // Какой пресет сейчас активен (если from/to совпали с одним из них)?
  const activePreset: PresetId | null = useMemo(() => {
    for (const p of PRESETS) {
      const r = p.range();
      if (r.from === from && r.to === to) return p.id;
    }
    return null;
  }, [from, to]);

  function applyPreset(id: PresetId): void {
    const r = rangeFromPreset(id);
    setFrom(r.from);
    setTo(r.to);
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Биллинг</h1>
          <p className="text-sm text-muted-foreground">
            Итог клиенту = фикс-цена связок (пропорц. периоду) + LLM × {data?.markup ?? 3}.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="inline-flex rounded-md border border-border p-1">
            {(['USD', 'EUR'] as Currency[]).map((c) => (
              <button
                key={c}
                onClick={() => setCurrency(c)}
                className={cn(
                  'rounded-sm px-3 py-1 text-xs font-medium transition-colors',
                  currency === c
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent/50',
                )}
              >
                {c}
              </button>
            ))}
          </div>
          {data?.fx && currency === 'EUR' && (
            <div className="text-xs text-muted-foreground">
              Курс: $1 = €{data.fx.usd_to_eur.toFixed(4)}{' '}
              <span className="text-muted-foreground/70">от {data.fx.date}</span>
              {data.fx.stale && (
                <Badge variant="warning" className="ml-2">
                  устаревший
                </Badge>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Range picker */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-4 pt-6">
          <div className="flex flex-wrap gap-1">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => applyPreset(p.id)}
                className={cn(
                  'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
                  activePreset === p.id
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <input
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            />
            <span className="text-xs text-muted-foreground">—</span>
            <input
              type="date"
              value={to}
              min={from}
              max={fmt(todayUtc())}
              onChange={(e) => setTo(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            />
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card>
          <CardContent className="flex items-center gap-2 pt-6 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            Ошибка загрузки: {(error as Error).message}
          </CardContent>
        </Card>
      )}

      {/* Сводка по выбранному периоду */}
      <section className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <TotalCard
          label="Фикс за период"
          value={
            data
              ? formatMoney(
                  currency === 'USD' ? data.totals.fixed_usd : data.totals.fixed_eur,
                  currency,
                )
              : '—'
          }
          hint="с даты каждой связки, в пределах периода"
        />
        <TotalCard
          label="LLM клиенту"
          value={data ? formatMoney(clientForCurrency(data.totals.period, currency), currency) : '—'}
          hint={`токены × ${data?.markup ?? 3}`}
        />
        <TotalCard
          label="Итого клиенту"
          value={
            data
              ? formatMoney(
                  currency === 'USD' ? data.totals.total_usd : data.totals.total_eur,
                  currency,
                )
              : '—'
          }
          hint={`за ${humanRange(from, to)}`}
          tone="success"
        />
        <TotalCard
          label="Долг всего"
          value={
            data
              ? formatMoney(
                  currency === 'USD' ? eurToUsd(totalDebtEur, data.fx.usd_to_eur) : totalDebtEur,
                  currency,
                )
              : '—'
          }
          hint="начислено − оплачено"
          tone={totalDebtEur > 0.01 ? 'danger' : 'success'}
        />
      </section>

      {/* Детализация по клиентам */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <CircleDollarSign className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-base font-semibold">Связки по клиентам</h2>
        </div>
        {isLoading ? (
          <Card>
            <CardContent className="pt-6">
              <TableSkeleton
                columns={['Связка', 'Токены', 'LLM клиенту', 'Цена/мес', 'Начисл. с', 'Итого']}
                rows={5}
              />
            </CardContent>
          </Card>
        ) : clientGroups.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              Нет связок.
            </CardContent>
          </Card>
        ) : (
          clientGroups.map((group) => (
            <ClientCostCard
              key={group.client}
              group={group}
              currency={currency}
              factor={data?.proration.factor ?? 1}
              usdToEur={data?.fx.usd_to_eur ?? 1}
              savePrice={(line, price) =>
                priceMutation.mutate({
                  type: line.connection_type,
                  id: line.connection_id,
                  price_eur: price,
                  label: line.label,
                  client_name: line.client_name,
                })
              }
              saveBilledSince={(line, billed_since) =>
                priceMutation.mutate({
                  type: line.connection_type,
                  id: line.connection_id,
                  billed_since,
                  label: line.label,
                  client_name: line.client_name,
                })
              }
              saveMarkup={(line, markup_x) =>
                priceMutation.mutate({
                  type: line.connection_type,
                  id: line.connection_id,
                  markup_x,
                  label: line.label,
                  client_name: line.client_name,
                })
              }
              onAddPayment={(amount_eur, paid_on, note) =>
                addPaymentMutation.mutate({
                  client_name: group.client,
                  amount_eur,
                  paid_on,
                  note,
                })
              }
              onDeletePayment={(id) => deletePaymentMutation.mutate(id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ClientCostCard({
  group,
  currency,
  factor,
  usdToEur,
  savePrice,
  saveBilledSince,
  saveMarkup,
  onAddPayment,
  onDeletePayment,
}: {
  group: {
    client: string;
    lines: BillingLine[];
    total: ClientTotal;
    billing: ClientBilling | null;
  };
  currency: Currency;
  factor: number;
  usdToEur: number;
  savePrice: (line: BillingLine, price: number | null) => void;
  saveBilledSince: (line: BillingLine, billed_since: string | null) => void;
  saveMarkup: (line: BillingLine, markup_x: number | null) => void;
  onAddPayment: (amount_eur: number, paid_on: string, note?: string) => void;
  onDeletePayment: (id: string) => void;
}): JSX.Element {
  const total = totalForCurrency(group.total, currency);
  const b = group.billing;
  const disp = (eur: number): number => eurDisplay(eur, currency, usdToEur);
  const hasBilling = b !== null && (b.accrued_eur > 0 || b.paid_eur > 0);

  const [showPay, setShowPay] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState(fmt(todayUtc()));
  const [payNote, setPayNote] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const { data: payments = [] } = useQuery({
    queryKey: ['payments', group.client],
    queryFn: () =>
      api.get<Payment[]>(`/admin/billing/payments?client=${encodeURIComponent(group.client)}`),
    enabled: showHistory,
  });

  function submitPayment(): void {
    const amt = Number(payAmount);
    if (!Number.isFinite(amt) || amt <= 0) return;
    onAddPayment(amt, payDate, payNote.trim() || undefined);
    setPayAmount('');
    setPayNote('');
    setShowPay(false);
  }

  return (
    <Card>
      <CardHeader className="gap-2 pb-2">
        <CardTitle className="flex flex-wrap items-baseline justify-between gap-2 text-base">
          <span>{group.client}</span>
          <span className="text-sm font-normal text-muted-foreground">
            итого/период{' '}
            <span className="font-semibold text-success">{formatMoney(total, currency)}</span>
          </span>
        </CardTitle>

        {/* Долг + оплата. Начисление — с даты каждой связки (см. столбец «начисл. с»). */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
          {hasBilling && b && (
            <>
              <span className="text-muted-foreground">
                Начислено:{' '}
                <span className="font-medium text-foreground">
                  {formatMoney(disp(b.accrued_eur), currency)}
                </span>
              </span>
              <span className="text-muted-foreground">
                Оплачено:{' '}
                <span className="font-medium text-foreground">
                  {formatMoney(disp(b.paid_eur), currency)}
                </span>
              </span>
              <Badge variant={b.debt_eur > 0.01 ? 'destructive' : 'success'}>
                Долг {formatMoney(disp(b.debt_eur), currency)}
              </Badge>
            </>
          )}
          {!hasBilling && (
            <span className="text-muted-foreground">
              Начисление не задано — проставьте дату «начисл. с» у связок.
            </span>
          )}
          <div className="ml-auto flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7" onClick={() => setShowPay((v) => !v)}>
              <Plus className="mr-1 h-3 w-3" /> платёж
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7"
              onClick={() => setShowHistory((v) => !v)}
            >
              история
              <ChevronDown
                className={cn('ml-1 h-3 w-3 transition-transform', showHistory && 'rotate-180')}
              />
            </Button>
          </div>
        </div>

        {/* Форма платежа */}
        {showPay && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/30 p-2 text-xs">
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="Сумма €"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
              className="h-7 w-28"
            />
            <Input
              type="date"
              value={payDate}
              onChange={(e) => setPayDate(e.target.value)}
              className="h-7 w-36"
            />
            <Input
              type="text"
              placeholder="Комментарий"
              value={payNote}
              onChange={(e) => setPayNote(e.target.value)}
              className="h-7 flex-1"
            />
            <Button size="sm" className="h-7" onClick={submitPayment}>
              Добавить
            </Button>
          </div>
        )}

        {/* История платежей */}
        {showHistory && (
          <div className="rounded-md border border-border bg-muted/20 p-2 text-xs">
            {payments.length === 0 ? (
              <div className="py-2 text-center text-muted-foreground">Платежей нет.</div>
            ) : (
              <ul className="space-y-1">
                {payments.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-2">
                    <span className="tabular-nums">
                      {p.paid_on} · {formatMoney(disp(p.amount_eur), currency)}
                      {p.note && <span className="text-muted-foreground"> — {p.note}</span>}
                    </span>
                    <button
                      onClick={() => onDeletePayment(p.id)}
                      className="text-muted-foreground hover:text-destructive"
                      title="Удалить платёж"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Связка</TableHead>
              <TableHead className="text-right">Токены</TableHead>
              <TableHead className="text-right">Наценка</TableHead>
              <TableHead className="text-right">LLM клиенту</TableHead>
              <TableHead className="text-right">Цена €/мес</TableHead>
              <TableHead className="text-right">Начисл. с</TableHead>
              <TableHead className="text-right">Итого</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {group.lines.map((line) => (
              <LineRow
                key={`${line.connection_type}-${line.connection_id}`}
                line={line}
                currency={currency}
                factor={factor}
                savePrice={savePrice}
                saveBilledSince={saveBilledSince}
                saveMarkup={saveMarkup}
              />
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function humanRange(from: string, to: string): string {
  if (from === to) return from;
  return `${from} — ${to}`;
}

function TotalCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'success' | 'danger';
}): JSX.Element {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div
          className={cn(
            'mt-2 text-2xl font-bold tabular-nums',
            tone === 'success' && 'text-success',
            tone === 'danger' && 'text-destructive',
          )}
        >
          {value}
        </div>
        {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

/** Типы связок, тратящих LLM-токены — только у них есть смысл в наценке. */
const LLM_CONNECTION_TYPES = new Set(['ai_summary', 'unison-chat']);

function LineRow({
  line,
  currency,
  factor,
  savePrice,
  saveBilledSince,
  saveMarkup,
}: {
  line: BillingLine;
  currency: Currency;
  factor: number;
  savePrice: (line: BillingLine, price: number | null) => void;
  saveBilledSince: (line: BillingLine, billed_since: string | null) => void;
  saveMarkup: (line: BillingLine, markup_x: number | null) => void;
}): JSX.Element {
  void factor;
  const agg = line.period;
  const tokens = agg.input_tokens + agg.output_tokens;
  const isLlm = LLM_CONNECTION_TYPES.has(line.connection_type);
  const [draft, setDraft] = useState<string>(
    line.price_eur_monthly === null ? '' : String(line.price_eur_monthly),
  );
  const [markupDraft, setMarkupDraft] = useState<string>(String(line.markup_x));

  function commit(): void {
    const trimmed = draft.trim();
    const next = trimmed === '' ? null : Number(trimmed);
    if (next !== null && (!Number.isFinite(next) || next < 0)) return;
    const cur = line.price_eur_monthly;
    if (next === cur) return; // без изменений
    savePrice(line, next);
  }

  // Наценка: пусто → сброс на глобальный дефолт (null). Значение → своя наценка.
  function commitMarkup(): void {
    const trimmed = markupDraft.trim();
    const next = trimmed === '' ? null : Number(trimmed);
    if (next !== null && (!Number.isFinite(next) || next <= 0)) return;
    if (next === line.markup_x) return; // без изменений
    saveMarkup(line, next);
  }

  return (
    <TableRow>
      <TableCell>
        <div className="font-medium">{line.label}</div>
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          {line.connection_type}
          {line.models.length > 0 ? ` · ${line.models.join(', ')}` : ''}
        </div>
      </TableCell>
      <TableCell className="text-right">
        {tokens === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <div className="text-sm tabular-nums">{formatTokens(tokens)}</div>
        )}
      </TableCell>
      <TableCell className="text-right">
        {isLlm ? (
          <Input
            type="number"
            min="1"
            step="0.5"
            inputMode="decimal"
            value={markupDraft}
            placeholder="3"
            onChange={(e) => setMarkupDraft(e.target.value)}
            onBlur={commitMarkup}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            className="h-8 w-16 text-right tabular-nums"
            title="Наценка × себестоимость LLM (пусто = глобальный ×3)"
          />
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {tokens === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          formatMoney(clientForCurrency(agg, currency), currency)
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex flex-col items-end gap-0.5">
          <Input
            type="number"
            min="0"
            step="1"
            inputMode="decimal"
            value={draft}
            placeholder="—"
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            className="h-8 w-24 text-right tabular-nums"
          />
          {line.price_eur_monthly !== null && (
            <span className="text-[11px] text-muted-foreground/70">
              → {formatMoney(fixedForCurrency(line, currency), currency)} за период
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <Input
          type="date"
          value={line.billed_since ?? ''}
          onChange={(e) => saveBilledSince(line, e.target.value === '' ? null : e.target.value)}
          className="h-8 w-36 text-xs"
          title="Начислять с этой даты"
        />
      </TableCell>
      <TableCell className="text-right">
        <span className="font-semibold tabular-nums text-success">
          {formatMoney(lineTotalForCurrency(line, currency), currency)}
        </span>
      </TableCell>
    </TableRow>
  );
}
